import { useMutation, useQueryClient } from "@tanstack/react-query";
import { serviceTicketService } from "./service-ticket.services";
import { SERVICE_TICKET_KEYS } from "./service-ticket.keys";
import { BOOKING_KEYS } from "../booking";
import { STAFF_AVAILABILITY_KEYS } from "../staff/staff-availability.queries";

export const useCreateServiceTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: serviceTicketService.createServiceTicket,
    onSuccess: (data, variables) => {
      // Invalidate service ticket queries
      qc.invalidateQueries({
        queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET],
      });

      // Invalidate metrics query (affects dashboard metrics when service tickets change)
      qc.invalidateQueries({
        queryKey: ['service-tickets', 'today-metrics'],
      });

      // Invalidate booking queries to update booking status (since adding a service ticket can change booking status)
      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });

      // Invalidate staff availability (affects booked hours and ServiceDatePicker)
      qc.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
      });

      // CRITICAL: When a new service ticket is added to a completed booking,
      // the booking status should automatically revert to "scheduled"
      const bookingId = data?.jobTicket?.id || variables.jobTicketId;
      if (bookingId) {
        console.log('Invalidating booking after new service ticket added:', bookingId);
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });

        // Aggressively refetch the booking to ensure status updates immediately
        qc.refetchQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });
      }
    },
  });
};

export const useUpdateServiceTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: serviceTicketService.updateServiceTicket,
    onSuccess: (data, variables) => {
      // Invalidate service ticket queries
      qc.invalidateQueries({
        queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET],
      });

      // Invalidate specific service ticket
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_SERVICE_TICKET(variables.id),
      });

      // Invalidate metrics query (affects dashboard metrics when service tickets change)
      qc.invalidateQueries({
        queryKey: ['service-tickets', 'today-metrics'],
      });

      // Invalidate booking queries to update booking status (since updating a service ticket status can change booking status)
      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });

      // Invalidate staff availability (affects booked hours and ServiceDatePicker)
      qc.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
      });

      // Invalidate specific booking - try multiple possible sources for the booking ID
      const bookingId = data?.jobTicket?.id || variables.jobTicketId;
      if (bookingId) {
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });
      }
    },
  });
};

export const useCompleteServiceTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: serviceTicketService.completeServiceTicket,
    onSuccess: (data, variables) => {
      // Invalidate specific service ticket queries
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_SERVICE_TICKET(variables.id),
      });

      // Invalidate service ticket lists
      qc.invalidateQueries({
        queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET, "list"],
      });

      // Invalidate metrics query (affects dashboard metrics when service tickets change)
      qc.invalidateQueries({
        queryKey: ['service-tickets', 'today-metrics'],
      });

      // Invalidate my service tickets (for assignments page)
      qc.invalidateQueries({
        queryKey: [`${SERVICE_TICKET_KEYS.SERVICE_TICKET}/myServices`],
      });

      // Invalidate ALL booking queries more aggressively
      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });

      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKING],
      });

      // Invalidate specific booking queries that matter for status updates
      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_MY_BOOKINGS(),
      });

      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_ALL_MY_RELATED_BOOKINGS(),
      });

      // Invalidate upcoming bookings with services (for assignments)
      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_UPCOMING_BOOKINGS_INCLUDING_SERVICES(),
      });

      // Invalidate staff availability (affects booked hours and ServiceDatePicker)
      qc.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
      });

      // CRITICAL: Invalidate the specific booking that contains this completed service ticket
      // This ensures the booking header status updates immediately
      const bookingId = data?.jobTicket?.id;
      if (bookingId) {
        console.log('Invalidating specific booking:', bookingId);
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });

        // Also refetch it immediately to ensure fresh data
        qc.refetchQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });
      }

      // Force a complete refresh by invalidating the entire query cache for bookings
      qc.refetchQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });
    },
  });
};

export const useDeleteServiceTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: serviceTicketService.deleteServiceTicket,
    onSuccess: (data, deletedServiceTicketId) => {
      // Invalidate service ticket queries
      qc.invalidateQueries({
        queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET],
      });

      // Invalidate metrics query (affects dashboard metrics when service tickets change)
      qc.invalidateQueries({
        queryKey: ['service-tickets', 'today-metrics'],
      });

      // Invalidate booking queries to update booking status (since removing a service ticket can change booking status)
      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });

      // Invalidate staff availability (affects booked hours and ServiceDatePicker)
      qc.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
      });

      // Invalidate specific booking if available in the response
      const bookingId = data?.jobTicket?.id;
      if (bookingId) {
        qc.invalidateQueries({
          queryKey: BOOKING_KEYS.GET_BOOKING(bookingId),
        });
      }

      // Note: If we don't have the jobTicketId in the delete response, the broad invalidation above
      // will ensure booking status updates when service tickets are deleted
    },
  });
};

export const useImportServiceTickets = () => {
  return useMutation({
    mutationFn: serviceTicketService.importServiceTickets,
  });
};

export const useCalculateEstimate = () => {
  return useMutation({
    mutationFn: serviceTicketService.calculateEstimate,
  });
};

export const useUpdateDailyMetricsOverrides = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ date, overrides }: { 
      date: string; 
      overrides: {
        staffHoursOverride?: number | null;
        vesselHoursOverride?: number | null;
        materialsCostOverride?: number | null;
      }
    }) => serviceTicketService.updateDailyMetricsOverrides(date, overrides),
    onSuccess: () => {
      // Invalidate daily metrics queries to get updated data with recalculated costs/profit
      qc.invalidateQueries({ queryKey: ['daily-metrics'] });
      // Invalidate today-metrics query which uses daily_metrics table for date ranges
      // This is safe because today-metrics is a specific endpoint, not the general service-tickets list
      qc.invalidateQueries({ 
        queryKey: ['service-tickets', 'today-metrics']
      });
      // Force refetch individual daily metrics to get updated costs/profit
      qc.refetchQueries({ queryKey: ['daily-metrics', 'individual'] });
    },
  });
};

export const useSyncDailyMetrics = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ date, startDate, endDate }: {
      date?: string;
      startDate?: string;
      endDate?: string;
    }) => serviceTicketService.syncDailyMetrics(date, startDate, endDate),
    onSuccess: () => {
      // Invalidate all related queries to get fresh data
      qc.invalidateQueries({ queryKey: ['daily-metrics'] });
      qc.invalidateQueries({ queryKey: ['service-tickets', 'today-metrics'] });
      qc.refetchQueries({ queryKey: ['daily-metrics', 'individual'] });
    },
  });
};

// ==========================================
// EXPO MODE MUTATIONS
// ==========================================

export const useSaveExpoDays = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceTicketId, days }: { serviceTicketId: string; days: import("~/types").TExpoDay[] }) =>
      serviceTicketService.saveExpoDays(serviceTicketId, { days }),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_EXPO_DAYS(variables.serviceTicketId),
      });
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_SERVICE_TICKET(variables.serviceTicketId),
      });
    },
  });
};

export const useGenerateExpoDaysPreview = () => {
  return useMutation({
    mutationFn: ({ serviceTicketId, startDate, endDate }: {
      serviceTicketId: string;
      startDate: string;
      endDate: string;
    }) => serviceTicketService.generateExpoDaysPreview(serviceTicketId, { startDate, endDate }),
  });
};

export const useCopyFromDayOne = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceTicketId, targetDayIds }: { serviceTicketId: string; targetDayIds: string[] }) =>
      serviceTicketService.copyFromDayOne(serviceTicketId, { targetDayIds }),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_EXPO_DAYS(variables.serviceTicketId),
      });
    },
  });
};

export const useApplyPositionToAllDays = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serviceTicketId, ...payload }: {
      serviceTicketId: string;
      positionId: string;
      quantity: number;
      startTime: string;
      duration: number;
      hourlyRate: number;
    }) => serviceTicketService.applyPositionToAllDays(serviceTicketId, payload),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_EXPO_DAYS(variables.serviceTicketId),
      });
    },
  });
};

export const useDeleteExpoDays = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (serviceTicketId: string) => serviceTicketService.deleteExpoDays(serviceTicketId),
    onSuccess: (data, serviceTicketId) => {
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_EXPO_DAYS(serviceTicketId),
      });
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_SERVICE_TICKET(serviceTicketId),
      });
    },
  });
};

// ==========================================
// BOOKING-LEVEL EXPO MODE MUTATIONS
// ==========================================

export const useSaveBookingExpoDays = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, days }: { bookingId: string; days: import("~/types").TExpoDay[] }) =>
      serviceTicketService.saveBookingExpoDays(bookingId, { days }),
    onSuccess: (data, variables) => {
      // Invalidate booking queries to get updated service tickets
      qc.invalidateQueries({
        queryKey: BOOKING_KEYS.GET_BOOKING(variables.bookingId),
      });
      qc.invalidateQueries({
        queryKey: [BOOKING_KEYS.BOOKINGS],
      });
      // Also invalidate service ticket queries
      qc.invalidateQueries({
        queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET],
      });
    },
  });
};

// ==========================================
// BULK INVENTORY MANAGEMENT
// ==========================================

export const useBulkManageInventory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: serviceTicketService.bulkManageInventory,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SERVICE_TICKET_KEYS.SERVICE_TICKET] });
      qc.invalidateQueries({ queryKey: [BOOKING_KEYS.BOOKING] });
      qc.invalidateQueries({ queryKey: [BOOKING_KEYS.BOOKINGS] });
    },
  });
};